/**
 * "Security" gadget (Verify): from the Quality overview (shared with Dependencies and Tests), the critical and high
 * vulnerabilities, the risky code and settings rated critical or high, the live secrets written in the code, and the
 * packages whose licence is not allowed. A check that has not run shows "—" with "not checked yet", never a 0.
 */
import React from 'react';
import PropTypes from 'prop-types';
import { securitySummary } from '../../../utils/homeGadgets/verify';
import GadgetCard, { GadgetBody, TabLink } from './GadgetCard';
import { useQualityOverview } from './sources';

function Tile({ value, label, testId }) {
  const known = value !== null && value !== undefined;
  return (
    <div className="gd-tile" data-testid={testId}>
      <strong className={known && value > 0 ? 'gd-bad' : known ? 'gd-ok' : ''}>{known ? value : '—'}</strong>
      <span>{known ? label : `${label}: not checked yet`}</span>
    </div>
  );
}
Tile.propTypes = { label: PropTypes.string, testId: PropTypes.string, value: PropTypes.number };

export default function SecurityGadget({ projectId, onOpenQuality = null }) {
  const { data, error, loading } = useQualityOverview(projectId);
  const s = securitySummary(data);
  return (
    <GadgetCard links={<TabLink onClick={onOpenQuality}>Verify › Quality</TabLink>} testId="security-gadget" title="Security">
      <GadgetBody
        data={data}
        empty={!s.ran}
        emptyText="The security checks have not run yet. Run them in Quality."
        error={error}
        errorText="The security checks could not be read just now."
        loading={loading}
        loadingText="Reading the security checks…"
      >
        {() => (
          <>
            <div className="gd-tiles">
              <Tile label="critical vulnerabilities" testId="security-critical" value={s.critical} />
              <Tile label="high vulnerabilities" testId="security-high" value={s.high} />
              <Tile label="live secrets in code" testId="security-secrets" value={s.liveSecrets} />
              <Tile label="licences not allowed" testId="security-licences" value={s.licences} />
            </div>
            {s.risky > 0 && <p className="gd-note gd-warn">{s.risky} risky {s.risky === 1 ? 'setting or line of code' : 'settings or lines of code'} rated critical or high.</p>}
            {s.unknownLicences > 0 && <p className="gd-note">{s.unknownLicences} {s.unknownLicences === 1 ? 'package has' : 'packages have'} no known licence.</p>}
          </>
        )}
      </GadgetBody>
    </GadgetCard>
  );
}

SecurityGadget.propTypes = { onOpenQuality: PropTypes.func, projectId: PropTypes.string.isRequired };
